import React, { useEffect } from 'react';

interface UseTableScrollListenersParams {
  primaryRef: React.MutableRefObject<HTMLDivElement | null>;
  syncScrollRefs: React.MutableRefObject<(HTMLDivElement | null)[]>;
  setIsScrolling: React.Dispatch<React.SetStateAction<boolean>>;
}

const PRIMARY_SOURCE_INDEX = -1;

export const useTableScrollListeners = ({
  primaryRef,
  syncScrollRefs,
  setIsScrolling,
}: UseTableScrollListenersParams) => {
  useEffect(() => {
    const syncListeners: Array<{ ref: HTMLDivElement; listener: EventListener }> = [];

    if (syncScrollRefs.current.length === 0 || primaryRef.current === null) {
      return;
    }

    const syncScrollPositions = (sourceRef: HTMLDivElement, sourceIndex: number) => {
      syncScrollRefs.current.forEach((ref, index) => {
        if (index !== sourceIndex && ref) {
          ref.scrollLeft = sourceRef.scrollLeft;
        }
      });

      if (primaryRef.current && primaryRef.current !== sourceRef) {
        primaryRef.current.scrollLeft = sourceRef.scrollLeft;
      }
    };

    const handleScroll = (sourceIndex: number) => {
      const sourceRef = syncScrollRefs.current[sourceIndex] || primaryRef.current;
      if (!sourceRef) {
        return;
      }

      setIsScrolling((sourceRef.scrollLeft || 0) > 0);
      syncScrollPositions(sourceRef, sourceIndex);
    };

    syncScrollRefs.current.forEach((ref, index) => {
      if (!ref) {
        return;
      }
      const listener = () => handleScroll(index);
      ref.addEventListener('scroll', listener);
      syncListeners.push({ ref, listener });
    });

    const primaryElement = primaryRef.current;
    const primaryListener = () => handleScroll(PRIMARY_SOURCE_INDEX);
    primaryElement?.addEventListener('scroll', primaryListener);

    return () => {
      syncListeners.forEach(({ ref, listener }) => {
        ref.removeEventListener('scroll', listener);
      });
      primaryElement?.removeEventListener('scroll', primaryListener);
    };
  }, [primaryRef, syncScrollRefs, setIsScrolling]);
};
